import { FormFieldTypes } from "types/enums";

export type ApiFieldType = "string" | "number" | "boolean";
export type ApiFieldConstraintKey = "example" | "values" | "min" | "max" | "regex";

export interface ApiFieldConstraint {
    key: ApiFieldConstraintKey;
    value: string;
}

export interface ApiFieldRow {
    system_name: string;
    type: ApiFieldType;
    is_required: boolean;
    description: string;
    note: string | null;
    constraints: ApiFieldConstraint[];
}

export interface ApiFieldSchema {
    type: ApiFieldType;
    required: boolean;
    description?: string;
    note?: string;
    example?: string | number | boolean;
    values?: string[];
    regex?: string;
    min?: number;
    max?: number;
}

const NUMBER_FIELD_TYPES: Array<FormFieldTypes | null> = [
    FormFieldTypes.Number,
    FormFieldTypes.OnlyNumber,
    FormFieldTypes.Slider,
    FormFieldTypes.SliderMoney,
    FormFieldTypes.CzkPrice,
];

const BOOLEAN_FIELD_TYPES: Array<FormFieldTypes | null> = [FormFieldTypes.Checkbox, FormFieldTypes.CheckboxTerms];

export const getApiFieldType = (type: FormFieldTypes | null): ApiFieldType => {
    if (NUMBER_FIELD_TYPES.includes(type)) return "number";
    if (BOOLEAN_FIELD_TYPES.includes(type)) return "boolean";
    return "string";
};

/**
 * Tries to convert a raw example_value string to the JSON type implied by the field's ApiFieldType.
 * Falls back to the original string when the conversion isn't possible (e.g. boolean field with "yes").
 */
export const coerceExampleValue = (value: string, type: ApiFieldType): string | number | boolean => {
    if (type === "boolean") {
        const normalized = value.trim().toLowerCase();
        if (normalized === "true") return true;
        if (normalized === "false") return false;
        return value;
    }

    if (type === "number") {
        const trimmed = value.trim();
        if (trimmed === "") return value;
        const numeric = Number(trimmed);
        return Number.isNaN(numeric) ? value : numeric;
    }

    return value;
};

const isFilled = (value: string | number | null | undefined): boolean =>
    value !== null && value !== undefined && value !== "";

/**
 * Allowed values for select/radio fields — the actual value that must be sent in the
 * request body, not the (potentially translated) visible_description shown in the form.
 */
const getAllowedValues = (field: App.Data.FormFieldData): string[] => [
    ...(field.formFieldSelectItems?.map((item) => item.value) ?? []),
    ...(field.formFieldRadioValues?.map((item) => item.value) ?? []),
];

const getConstraints = (field: App.Data.FormFieldData): ApiFieldConstraint[] => {
    const constraints: ApiFieldConstraint[] = [];
    const pushIfFilled = (key: ApiFieldConstraintKey, value: string | number | null | undefined) => {
        if (isFilled(value)) constraints.push({ key, value: String(value) });
    };

    pushIfFilled("example", field.example_value);

    const allowedValues = getAllowedValues(field);
    if (allowedValues.length > 0) {
        constraints.push({ key: "values", value: allowedValues.join(", ") });
    }

    pushIfFilled("min", field.min);
    pushIfFilled("max", field.max);
    pushIfFilled("regex", field.validation_regex);

    return constraints;
};

/**
 * doc_note is often left equal to (or a near-duplicate of) visible_description on existing
 * fields, so we drop it whenever it doesn't add anything beyond the description.
 */
const getNote = (field: App.Data.FormFieldData): string | null => {
    if (!field.doc_note) return null;
    if (field.doc_note.trim().toLowerCase() === field.visible_description.trim().toLowerCase()) return null;
    return field.doc_note;
};

export const buildApiFieldRows = (fields: App.Data.FormFieldData[]): ApiFieldRow[] =>
    fields
        .filter((field) => !!field.system_name)
        .map((field) => ({
            system_name: field.system_name,
            type: getApiFieldType(field.type),
            is_required: field.is_required,
            description: field.visible_description,
            note: getNote(field),
            constraints: getConstraints(field),
        }));

/**
 * Builds a plain (non-spec) JSON description of the request body, keyed by system_name —
 * meant to be copied to the clipboard, not rendered. Unlike JSON Schema, `required` lives
 * directly on each field instead of a separate top-level array.
 */
export const buildApiFieldsSchema = (fields: App.Data.FormFieldData[]): Record<string, ApiFieldSchema> => {
    const schema: Record<string, ApiFieldSchema> = {};

    for (const field of fields) {
        if (!field.system_name) continue;

        const type = getApiFieldType(field.type);
        const property: ApiFieldSchema = { type, required: field.is_required };

        if (field.visible_description) property.description = field.visible_description;

        const note = getNote(field);
        if (note) property.note = note;

        if (field.example_value) property.example = coerceExampleValue(field.example_value, type);

        const allowedValues = getAllowedValues(field);
        if (allowedValues.length > 0) property.values = allowedValues;

        if (field.validation_regex) property.regex = field.validation_regex;

        if (isFilled(field.min)) property.min = Number(field.min);
        if (isFilled(field.max)) property.max = Number(field.max);

        schema[field.system_name] = property;
    }

    return schema;
};
